import { Box, HStack, Input, Text } from '@chakra-ui/react'
import { memo, useCallback } from 'react'

import { ListToolbar } from '@mangovoice/mango-ui'
import type { TranslateFn } from '@mangovoice/mango-ui'

import { useHotelsI18n } from '../../i18n'
import type { PageSize } from '../../utils/pagination'

export type { PageSize }

const TABLE_PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500]

const TOOLBAR_FALLBACKS: Record<string, string> = {
  'hotels.pagination.resultsPerPage': 'Results per page:',
  'hotels.pagination.all': 'All',
  'hotels.pagination.previousPage': 'Previous page',
  'hotels.pagination.nextPage': 'Next page',
  'hotels.pagination.jumpToPage': 'Jump to page {{page}}',
  'hotels.pagination.recordRange': '{{start}}—{{end}} of {{total}}',
  'hotels.pagination.recordRangeEmpty': '0 of 0',
}

interface TableToolbarProps {
  /** Total number of items (unfiltered) */
  totalCount: number
  /** Number of items after search filtering */
  filteredCount: number
  /** Current page (1-indexed) */
  currentPage: number
  /** Items per page */
  pageSize: PageSize
  /** Search query string */
  searchQuery: string
  /** Callback when page changes */
  onPageChange: (page: number) => void
  /** Callback when page size changes */
  onPageSizeChange: (size: PageSize) => void
  /** Callback when search query changes */
  onSearchChange: (query: string) => void
  /** Search placeholder text */
  searchPlaceholder?: string
}

/**
 * Reusable table toolbar with search input and shared list pagination.
 *
 * Used by ExtensionActivationList and IncomingCallsTable.
 */
export const TableToolbar = memo<TableToolbarProps>(
  ({
    totalCount,
    filteredCount,
    currentPage,
    pageSize,
    searchQuery,
    onPageChange,
    onPageSizeChange,
    onSearchChange,
    searchPlaceholder,
  }) => {
    const { text } = useHotelsI18n()
    const t: TranslateFn = useCallback(
      (key, opts) =>
        text(
          key,
          TOOLBAR_FALLBACKS[key] ?? key,
          opts as Record<string, string | number> | undefined,
        ),
      [text],
    )
    const effectiveSearchPlaceholder =
      searchPlaceholder ?? text('hotels.common.search', 'Search...')

    return (
      <Box>
        <HStack
          justify="space-between"
          align="center"
          px={4}
          py={2}
          borderBottomWidth={1}
          borderColor="border.default"
          bg="bg.subtle"
          flexWrap="wrap"
          gap={3}
        >
          <HStack gap={2} align="center">
            <Text fontSize="sm" color="fg.subtle" flexShrink={0}>
              {text('hotels.common.search', 'Search')}:
            </Text>
            <Input
              size="sm"
              w="200px"
              borderRadius="l2"
              placeholder={effectiveSearchPlaceholder}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              bg="bg.default"
              borderColor="border.default"
            />
          </HStack>

          <Text fontSize="sm" color="fg.subtle">
            {filteredCount === totalCount
              ? text('hotels.tableToolbar.entriesOnly', '{{count}} entries', {
                  count: totalCount,
                })
              : text(
                  'hotels.tableToolbar.entriesFiltered',
                  '{{filtered}} of {{total}} entries',
                  {
                    filtered: filteredCount,
                    total: totalCount,
                  },
                )}
          </Text>
        </HStack>

        <Box px={4} bg="bg.subtle">
          <ListToolbar
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
            currentPage={currentPage}
            totalCount={filteredCount}
            onPageChange={onPageChange}
            isLoading={false}
            showToolbar
            showRecordRange
            pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
            showAllOption
            maxAllThreshold={Number.MAX_SAFE_INTEGER}
            size="sm"
            t={t}
            i18nPrefix="hotels.pagination"
          />
        </Box>
      </Box>
    )
  },
)

TableToolbar.displayName = 'TableToolbar'
